import { Box, Paper, Stack, Typography } from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useMemo } from 'react';
import type { CompetitionPublic, OverallRanking, Result } from '../../types/results';
import type { Category, Lane, Team } from '../../types/domain';
import { MEDAL_COLORS, bestAttemptPerTeam } from './BoardShared';
import { formatTime } from '../../utils/timeFormat';

interface ColumnsBoardProps {
  competition: CompetitionPublic;
  results: Result[];
  overall: OverallRanking[];
  allTeams: Team[] | undefined;
}

/**
 * Vysledkova tabule: sloupec pro kazdou kategorii, jedna tabulka na kategorii.
 * Radek = druzstvo, sloupce = disciplíny + celkove poradi.
 * Urceno pro plat / televizi / projektor (FullHD 1920×1080).
 */
export const ColumnsBoard = ({ competition, results, overall, allTeams }: ColumnsBoardProps) => {
  const categories = competition.categories;

  // Density je fixni — velikosti radku a fontu zustavaji stejne bez ohledu na pocet druzstev.
  // Pro >16 druzstev v kategorii prepneme na dense, aby se vsechny vesly.
  const density = useMemo(() => {
    let maxTeams = 0;
    for (const cat of categories) {
      let n = 0;
      if (allTeams) {
        n = allTeams.filter((t) => t.categoryId === cat.id).length;
      } else {
        const teamIds = new Set<number>();
        for (const r of results) {
          if (cat.disciplines.some((d) => d.id === r.disciplineId)) {
            teamIds.add(r.teamId);
          }
        }
        n = teamIds.size;
      }
      if (n > maxTeams) maxTeams = n;
    }
    if (maxTeams > 16) return 'dense' as const;
    return 'compact' as const;
  }, [categories, allTeams, results]);

  if (categories.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">Soutěž zatím nemá nastavené kategorie.</Typography>
      </Paper>
    );
  }

  const showOverall = competition.overallRankingVisible;
  const isFinished = competition.status === 'FINISHED';

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: `repeat(${categories.length}, minmax(0, 1fr))`,
        gap: 1.25,
        overflow: 'hidden',
      }}
    >
      {categories.map((cat) => {
        const ranking = showOverall ? overall.find((o) => o.categoryId === cat.id) : undefined;
        return (
          <CategoryMatrix
            key={cat.id}
            category={cat}
            results={results}
            ranking={ranking}
            allTeams={allTeams}
            showOverall={!!showOverall}
            isFinished={isFinished}
            density={density}
            showLaneDetails={!!competition.boardShowLaneDetails}
          />
        );
      })}
    </Box>
  );
};

type Density = 'comfy' | 'compact' | 'dense';

const DENSITY: Record<
  Density,
  {
    rowFs: string;
    subFs: string;
    headerFs: string;
    discFs: string;
    catFs: string;
    rowPy: number;
    catPy: number;
  }
> = {
  // Velikosti optimalizovane pro 27" monitor na soutezi (divak ~3-5 m).
  // Lane details jsou vedle hlavniho casu, takze nemeni vysku radku —
  // hlavni cas muze byt vetsi nez v predchozi verzi.
  comfy: { rowFs: '2.4rem', subFs: '0.85rem', headerFs: '1.05rem', discFs: '1.3rem', catFs: '2rem', rowPy: 0.85, catPy: 1.2 },
  compact: { rowFs: '1.75rem', subFs: '0.72rem', headerFs: '0.95rem', discFs: '1.1rem', catFs: '1.6rem', rowPy: 0.35, catPy: 0.75 },
  dense: { rowFs: '1.4rem', subFs: '0.62rem', headerFs: '0.82rem', discFs: '0.95rem', catFs: '1.25rem', rowPy: 0.15, catPy: 0.5 },
};

interface LaneTime {
  label: string;
  timeMs: number | null;
  isNp: boolean;
}

interface TeamRow {
  teamId: number;
  teamName: string;
  startNumber: number;
  overallRank: number | null;
  overallPoints: number | null;
  disciplineCells: Array<{
    disciplineId: number;
    timeFinalFormatted: string;
    timeFinalMs: number | null;
    isNp: boolean;
    rank: number | null;
    lanes: LaneTime[];
    timeFormat: 'SS_CC' | 'MM_SS_CC';
  }>;
}

const LANE_LABEL: Record<Lane, string> = {
  LP: 'LP',
  PP: 'PP',
  TRACK_1: 'D1',
  TRACK_2: 'D2',
};

/** Z attempts zapsaneho vysledku vytahne per-lane casy (nejlepsi mereni z casomericu per lane). */
function extractLaneTimes(result: Result | undefined, laneOrder: Lane[]): LaneTime[] {
  if (!result) return [];
  const byLane = new Map<Lane, { ms: number | null; isNp: boolean }>();
  for (const a of result.attempts ?? []) {
    const existing = byLane.get(a.lane);
    if (a.isNp) {
      if (!existing) byLane.set(a.lane, { ms: null, isNp: true });
      continue;
    }
    if (a.timeMs == null || a.timeMs <= 0) continue;
    if (!existing || existing.ms == null || (existing.ms != null && a.timeMs > existing.ms)) {
      // 2 casomerici: pri rozdilu bereme HORSI (max) — shodne s backendem
      byLane.set(a.lane, { ms: existing?.ms != null ? Math.max(existing.ms, a.timeMs) : a.timeMs, isNp: false });
    }
  }
  // Trestne sekundy se pricitaji k urednimu casu (max z drah + penalty).
  // Pri zobrazeni per-lane chceme konzistenci s urednim casem:
  // kazda draha = raw cas + penalty; max(D1+pen, D2+pen) = max(D1,D2)+pen = uredni cas.
  const penaltyMs = (result.penaltySeconds ?? 0) * 1000;
  return laneOrder
    .filter((l) => byLane.has(l))
    .map((l) => {
      const entry = byLane.get(l)!;
      const timeWithPenalty = entry.ms != null ? entry.ms + penaltyMs : null;
      return { label: LANE_LABEL[l], timeMs: timeWithPenalty, isNp: entry.isNp };
    });
}

function lanesForDisciplineType(type: string): Lane[] {
  if (type === 'POZARNI_UTOK' || type === 'POZARNI_UTOK_CTIF') return ['LP', 'PP'];
  if (type === 'STAFETA_DVOJIC') return ['TRACK_1', 'TRACK_2'];
  return ['TRACK_1'];
}

const CategoryMatrix = ({
  category,
  results,
  ranking,
  allTeams,
  showOverall,
  isFinished,
  density,
  showLaneDetails,
}: {
  category: Category;
  results: Result[];
  ranking: OverallRanking | undefined;
  allTeams: Team[] | undefined;
  showOverall: boolean;
  isFinished: boolean;
  density: Density;
  showLaneDetails: boolean;
}) => {
  const d = DENSITY[density];

  const rows: TeamRow[] = useMemo(() => {
    const catTeams = (allTeams ?? []).filter((t) => t.categoryId === category.id);
    const byTeam = new Map<number, TeamRow>();
    for (const t of catTeams) {
      byTeam.set(t.id, {
        teamId: t.id,
        teamName: t.name,
        startNumber: t.startNumber,
        overallRank: null,
        overallPoints: null,
        disciplineCells: category.disciplines.map((disc) => ({
          disciplineId: disc.id,
          timeFinalFormatted: '—',
          timeFinalMs: null,
          isNp: false,
          rank: null,
          lanes: [],
          timeFormat: (disc.timeFormat ?? 'MM_SS_CC') as 'SS_CC' | 'MM_SS_CC',
        })),
      });
    }
    // Fallback: pokud nemame allTeams, postavime z vysledku
    if (catTeams.length === 0) {
      const seen = new Set<number>();
      for (const r of results) {
        if (!category.disciplines.some((d) => d.id === r.disciplineId)) continue;
        if (seen.has(r.teamId)) continue;
        seen.add(r.teamId);
        byTeam.set(r.teamId, {
          teamId: r.teamId,
          teamName: r.teamName,
          startNumber: r.teamStartNumber,
          overallRank: null,
          overallPoints: null,
          disciplineCells: category.disciplines.map((disc) => ({
            disciplineId: disc.id,
            timeFinalFormatted: '—',
            timeFinalMs: null,
            isNp: false,
            rank: null,
            lanes: [],
            timeFormat: (disc.timeFormat ?? 'MM_SS_CC') as 'SS_CC' | 'MM_SS_CC',
          })),
        });
      }
    }

    // Naplnit buňky disciplin: best pokus per team
    for (const disc of category.disciplines) {
      const discResults = results.filter((r) => r.disciplineId === disc.id);
      const best = bestAttemptPerTeam(discResults);
      const laneOrder = lanesForDisciplineType(disc.type);
      for (const r of best) {
        const row = byTeam.get(r.teamId);
        if (!row) continue;
        const cell = row.disciplineCells.find((c) => c.disciplineId === disc.id);
        if (!cell) continue;
        cell.timeFinalFormatted = r.timeFinalFormatted;
        cell.timeFinalMs = r.timeFinalMs;
        cell.isNp = !!r.isNp;
        cell.rank = r.rank ?? null;
        if (showLaneDetails && laneOrder.length > 1) {
          cell.lanes = extractLaneTimes(r, laneOrder);
        }
      }
    }

    // Overall
    if (ranking) {
      for (const t of ranking.teams) {
        const row = byTeam.get(t.teamId);
        if (!row) continue;
        row.overallRank = t.finalRank;
        row.overallPoints = t.totalPoints;
      }
    }

    // Matice na tabuli vzdy razena podle startovniho cisla — divak vidi prubeh soutezeni
    // v poradi, v jakem druzstva startuji. Celkove poradi je v samostatnem sloupci.
    const arr = Array.from(byTeam.values());
    arr.sort((a, b) => a.startNumber - b.startNumber);
    return arr;
  }, [category, results, allTeams, ranking, showLaneDetails]);

  // Grid template: [rank] [team] [per-discipline: time+rank] [overall body]
  // Každá disciplína = 1 sloupec; overall = 1 sloupec (pokud showOverall)
  const discCols = category.disciplines.length;
  const gridTemplate = [
    '44px', // overall rank / poradove
    'minmax(0, 1fr)', // team name
    ...Array(discCols).fill('minmax(140px, 1.1fr)'),
    ...(showOverall && ranking ? ['minmax(60px, 0.6fr)'] : []),
  ].join(' ');

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: d.catPy,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          borderBottom: '2px solid',
          borderColor: 'primary.dark',
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: d.catFs,
            lineHeight: 1.15,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textAlign: 'center',
          }}
        >
          {category.name}
        </Typography>
      </Box>

      {/* Column headers */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridTemplate,
          alignItems: 'end',
          columnGap: 2,
          px: 1,
          py: 0.4,
          bgcolor: 'action.hover',
          borderBottom: '2px solid',
          borderColor: 'primary.main',
        }}
      >
        <Typography
          sx={{
            fontSize: d.headerFs,
            fontWeight: 800,
            textAlign: 'center',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          č.
        </Typography>
        <Typography
          sx={{
            fontSize: d.headerFs,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          Družstvo
        </Typography>
        {category.disciplines.map((disc) => (
          <Typography
            key={disc.id}
            sx={{
              fontSize: d.discFs,
              fontWeight: 800,
              textAlign: 'center',
              letterSpacing: '0.02em',
              color: 'primary.main',
              lineHeight: 1.15,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
            title={disc.name}
          >
            {shortDisciplineName(disc.name)}
          </Typography>
        ))}
        {showOverall && ranking && (
          <Typography
            sx={{
              fontSize: d.headerFs,
              fontWeight: 800,
              textAlign: 'center',
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'primary.main',
            }}
          >
            Poř.
          </Typography>
        )}
      </Box>

      {/* Rows — pevna vyska radku, ne flex-grow (text musi vypadat stejne pro 5 i 15 druzstev).
          Globalni velikost se meni jen v Nastaveni (boardFontScale / CSS zoom). */}
      <Stack sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.25 }}>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ p: 2, textAlign: 'center' }}>
            — zatím žádná družstva —
          </Typography>
        ) : (
          rows.map((row, idx) => {
            const medalRank = row.overallRank;
            const medal = medalRank != null ? MEDAL_COLORS[medalRank] : null;
            return (
              <Box
                key={row.teamId}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: gridTemplate,
                  alignItems: 'center',
                  columnGap: 2,
                  px: 1,
                  py: d.rowPy,
                  flexShrink: 0,
                  bgcolor: medal ? `${medal.bg}33` : idx % 2 === 0 ? 'transparent' : 'action.hover',
                  borderLeft: medal ? `5px solid ${medal.bg}` : '5px solid transparent',
                }}
              >
                {/* Startovni cislo */}
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: d.rowFs,
                    fontVariantNumeric: 'tabular-nums',
                    textAlign: 'center',
                    color: medal ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {row.startNumber}
                </Typography>
                {/* Team name */}
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: d.rowFs,
                    overflow: 'hidden',
                    lineHeight: 1.15,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}
                >
                  {row.teamName}
                </Typography>
                {/* Discipline cells — vlevo sloupec lane casu, uprostred hlavni cas + pill */}
                {row.disciplineCells.map((cell) => {
                  const discMedal = cell.rank != null ? MEDAL_COLORS[cell.rank] : null;
                  return (
                    <Box
                      key={cell.disciplineId}
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1,
                        lineHeight: 1.05,
                        minWidth: 0,
                      }}
                    >
                      {showLaneDetails && cell.lanes.length > 0 && (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            justifyContent: 'center',
                            gap: 0.1,
                            lineHeight: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            borderRight: '1px dashed',
                            borderColor: 'divider',
                            pr: 0.75,
                          }}
                        >
                          {cell.lanes.map((l) => {
                            const laneFormat: 'SS_CC' | 'MM_SS_CC' = cell.timeFormat;
                            const isOfficial =
                              !l.isNp &&
                              !cell.isNp &&
                              l.timeMs != null &&
                              cell.timeFinalMs != null &&
                              l.timeMs === cell.timeFinalMs;
                            return (
                              <Box
                                key={l.label}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'baseline',
                                  justifyContent: 'flex-start',
                                  gap: 0.3,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: `calc(${d.subFs} * 0.85)`,
                                    fontWeight: 800,
                                    letterSpacing: '0.02em',
                                    color: isOfficial ? 'primary.main' : 'text.disabled',
                                    textTransform: 'uppercase',
                                    flexShrink: 0,
                                  }}
                                >
                                  {l.label}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: d.subFs,
                                    fontVariantNumeric: 'tabular-nums',
                                    fontWeight: isOfficial ? 800 : 500,
                                    color: l.isNp
                                      ? 'error.main'
                                      : isOfficial
                                      ? 'text.primary'
                                      : 'text.disabled',
                                  }}
                                >
                                  {l.isNp || l.timeMs == null ? 'NP' : formatTime(l.timeMs, laneFormat)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      )}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: 0.75,
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: 'monospace',
                            fontWeight: 700,
                            fontSize: d.rowFs,
                            fontVariantNumeric: 'tabular-nums',
                            color: cell.isNp ? 'error.main' : 'text.primary',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {cell.timeFinalFormatted}
                        </Typography>
                        {cell.rank != null && !cell.isNp && (
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minWidth: '2.2em',
                              height: '2.2em',
                              paddingInline: '0.5em',
                              borderRadius: '999px',
                              bgcolor: discMedal ? discMedal.bg : 'action.selected',
                              color: discMedal ? discMedal.fg : 'text.primary',
                              fontSize: `calc(${d.rowFs} * 0.55)`,
                              fontWeight: 900,
                              fontFamily: 'monospace',
                              fontVariantNumeric: 'tabular-nums',
                              letterSpacing: 0,
                              border: discMedal ? 'none' : '1px solid',
                              borderColor: 'divider',
                              lineHeight: 1,
                              flexShrink: 0,
                              boxSizing: 'border-box',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'block',
                                width: '100%',
                                textAlign: 'center',
                              }}
                            >
                              {cell.rank}
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                })}
                {/* Celkove poradi (bez bodu) */}
                {showOverall && ranking && (
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      alignItems: 'center',
                      justifyItems: 'end',
                      columnGap: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontWeight: 900,
                        fontSize: d.rowFs,
                        fontVariantNumeric: 'tabular-nums',
                        textAlign: 'right',
                        color: medal ? 'text.primary' : row.overallRank != null && row.overallRank <= 3 ? 'primary.main' : 'text.primary',
                      }}
                    >
                      {row.overallRank != null ? `${row.overallRank}.` : '–'}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        width: `calc(${d.rowFs} * 1.1)`,
                      }}
                    >
                      {isFinished && row.overallRank != null && row.overallRank <= 3 && (
                        <EmojiEventsIcon
                          sx={{
                            fontSize: `calc(${d.rowFs} * 1.1)`,
                            color: MEDAL_COLORS[row.overallRank]?.bg ?? '#D4AF37',
                            filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))',
                          }}
                          aria-label={`${row.overallRank}. místo`}
                        />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })
        )}
      </Stack>
    </Paper>
  );
};

/** Zkraceni dlouhych nazvu disciplín pro tabuli (aby se vesly do uzkeho sloupce). */
function shortDisciplineName(name: string): string {
  const map: Record<string, string> = {
    'Požární útok': 'PÚ',
    'Požární útok s překážkami CTIF': 'PÚ CTIF',
    'Štafeta požárních dvojic': 'Š. dvojic',
    'Štafeta 4×60 m': 'Š. 4×60',
    'Štafeta 4×100 m s překážkami': 'Š. 4×100',
    'Štafeta CTIF (400 m)': 'Š. CTIF',
    'Běh na 60 m s překážkami': 'Běh 60 m',
    'Běh na 100 m s překážkami': 'Běh 100 m',
    'Výstup na cvičnou věž (4. patro)': 'Věž 4. p.',
    'Výstup na cvičnou věž (1. patro)': 'Věž 1. p.',
  };
  return map[name] ?? name;
}
